import { Box, Button, Grid, styled } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import DraftsIcon from '@mui/icons-material/Drafts';
import Loading from 'components/Loading';
import { TRACKABLE_EVENTS, trackEvent } from 'helpers/trackerHelpers';
import {
    useMarkAllNotificationRead,
    useMarkSingleNotificationRead,
} from './hooks';
import NotificationCellRenderer from './NotificationCellRenderer';
import { Notification, NotificationColumn } from './types';

type Props = {
    notifications: Array<Notification> | undefined;
    isUnreadNotification: boolean | undefined;
    onRowClick: () => void;
};

const StyLabel = styled('span', {
    name: 'StyLabel',
})({
    whiteSpace: 'nowrap',
    fontSize: '0.83rem',
});

const StyHeader = styled('span', {
    name: 'StyHeader',
})(() => ({
    fontWeight: 600,
    letterSpacing: '0.42855px',
    color: '#3F4750',
}));

const StyContainer = styled(Grid, {
    name: 'StyContainer',
})(({ theme }) => ({
    width: '100%',
    height: '281px',
    background: theme.palette.lightGrey,
    '& div.ag-center-cols-container': {
        width: '100%',
    },
    '& div.ag-header': {
        border: 'unset',
    },
    '& div.ag-row': {
        border: 'unset',
    },
    '& div.ag-header-cell:not(:last-child) div.ag-cell:not(:last-child)': {
        borderRight: 'none',
    },
    '& div.ag-cell': {
        padding: 'unset',
        border: 'unset',
        width: '100%',
    },
    '& div.ag-ltr .ag-cell': {
        border: 'unset',
    },
    '& div.ag-paging-panel': {
        border: 'unset',
        height: '40px',
    },
}));

// GRID
const columns: ColDef[] = [
    {
        field: '',
        cellRenderer: 'notificationCellRenderer',
        cellRendererParams: {
            colId: NotificationColumn.CONTENT,
        },
        width: 520,
    },
    {
        field: '',
        cellRenderer: 'notificationCellRenderer',
        cellRendererParams: {
            colId: NotificationColumn.DATE,
        },
        width: 58,
    },
];

const gridOptions = {
    suppressRowClickSelection: true, // Disable row selection on row click
    suppressCellSelection: true, // Disable cell selection
    getContextMenuItems: () => [], // Remove context menu on rightClick
    autoHeight: true,
    autoWidth: true,
};

export const NotificationGrid = ({
    notifications,
    isUnreadNotification,
    onRowClick,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    // HOOKS
    const { mutate: markAllRead } = useMarkAllNotificationRead();

    const { mutate: markSingleRead } = useMarkSingleNotificationRead();

    if (notifications === undefined || isUnreadNotification === undefined)
        return <Loading />;

    return (
        <Flex direction="column" alignContent="center">
            <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: '0.5rem' }}
            >
                <StyHeader>{t('notification.panel.header')}</StyHeader>
                <Button
                    variant="outlined"
                    onClick={() => {
                        markAllRead();
                        trackEvent(
                            TRACKABLE_EVENTS.global.ux.notificationsCenter
                                .markAllAsRead,
                        );
                    }}
                    startIcon={<DraftsIcon />}
                    disabled={!isUnreadNotification}
                >
                    <StyLabel>{t('notification.panel.markAllRead')}</StyLabel>
                    <Box sx={{ ml: 'auto', display: 'inline-flex' }} />
                </Button>
            </Flex>
            <StyContainer className="ag-theme-material">
                <AgGridReact
                    gridOptions={gridOptions}
                    headerHeight={0}
                    rowData={notifications}
                    overlayNoRowsTemplate={t('notification.panel.noRows')}
                    components={{
                        notificationCellRenderer: NotificationCellRenderer,
                    }}
                    pagination
                    paginationPageSize={5}
                    columnDefs={columns}
                    suppressHorizontalScroll
                    onRowClicked={({ data }: { data: Notification }) => {
                        markSingleRead(data.id);
                        trackEvent(
                            TRACKABLE_EVENTS.global.ux.notificationsCenter
                                .clickNotification,
                            {
                                entityLabel: data.entityLabel,
                                actionType: data.actionType,
                                actorName: data.actorName,
                                notificationText: t(
                                    `notification.reasons.${data.key}`,
                                    {
                                        actorName: data.actorName,
                                        redirectTo: data.redirectTo,
                                        entityLabel: data.entityLabel,
                                    },
                                ),
                                key: data.key,
                                redirectTo: data.redirectTo,
                                createdAt: data.createdAt,
                            },
                        );
                        onRowClick();
                    }}
                />
            </StyContainer>
        </Flex>
    );
};
